import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Receipt, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ReceiptUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/receipt-upload/${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setError("This upload link is invalid."));
  }, [token]);

  const upload = async () => {
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/public/receipt-upload/${encodeURIComponent(token)}`, fd);
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const closed = info && (info.used || info.expired);
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="receipt-upload-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8 card-crisp">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center"><Zap className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="font-heading font-bold text-lg">LeadForge</span>
        </div>
        <h1 className="font-heading text-2xl font-bold mb-1 flex items-center gap-2"><Receipt className="w-5 h-5 text-[#FF5A00]" />Upload receipt</h1>
        {info?.business_name && <p className="text-sm text-zinc-600 mb-6">For {info.business_name}. This link works once and expires in 30 minutes.</p>}
        {done ? (
          <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="receipt-upload-done">
            <CheckCircle2 className="w-5 h-5" /> Receipt uploaded. Go back to ChatGPT and say "done".
          </div>
        ) : closed ? (
          <p className="text-sm text-amber-700" data-testid="receipt-upload-closed">This link has {info.used ? "already been used" : "expired"}. Ask ChatGPT for a new one.</p>
        ) : info ? (
          <>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm mb-4 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-2" data-testid="receipt-upload-input" />
            <Button className="w-full bg-[#FF5A00] hover:bg-[#e65100] text-white" disabled={!file || busy} onClick={upload} data-testid="receipt-upload-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
            </Button>
          </>
        ) : !error && <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />}
        {error && <p className="text-sm text-rose-600 mt-3" data-testid="receipt-upload-error">{error}</p>}
      </div>
    </div>
  );
}
